<template>
  <div class="wealth-content">
    <SkeletonLoader v-if="isAccountAmountLoading">
      <div class="wealth-content__account-amount__loader" />
    </SkeletonLoader>
    <ContentDisplay
      v-else-if="!hasAccountAmountError && accountAmount"
      class="wealth-content__account-amount"
    >
      <template #content-block-1>
        <ListDisplay title="Saldo em conta" value="" align-vertical>
          <template #value-slot>
            <Money>{{ accountAmount }}</Money>
          </template>
        </ListDisplay>
      </template>
    </ContentDisplay>
    <NavigationPanelWrapper>
      <NavigationPanel>
        <div slot-scope="{ setPanel }">
          <WealthSummary
            data-testid="wealth-summary"
            class="wealth-content__section"
            :show-config="false"
            @set-panel="
              () => {
                setPanel(1);
              }
            "
            @tracking="trackEvent"
          />

          <div class="wealth-content__section">
            <PortfoliosList />
            <Feature :name="FeatureFlags.DISABLE_WARREN_BUILDER">
              <template v-if="showCreateWalletButton" #fallback>
                <Row
                  class="portfolio-actions"
                  :chevron="true"
                  :has-interaction="true"
                  @click="goToBuilder"
                >
                  <div slot="icon-left">
                    <InitialAvatarIcon
                      class="wealth-content__icon"
                      icon="EF0011"
                      size="medium"
                    />
                  </div>
                  <div slot="top-left">Criar carteira</div>
                </Row>
              </template>
            </Feature>
          </div>
        </div>
      </NavigationPanel>

      <NavigationPanel>
        <div slot-scope="{ setPanel }">
          <div class="wealth-content__navbar-wrapper">
            <div class="wealth-content__navbar">
              <BackButton @click="setPanel(0)" />
              <Heading :level="5">Alocação</Heading>
              <div class="wealth-content__navbar__box" />
            </div>
          </div>
          <div
            v-if="isProductAllocationsLoading"
            class="wealth-content__allocation-loader"
          >
            <SpinnerLoader :size="48" />
          </div>
          <HelperViewStatus
            v-else-if="hasProductAllocationsError"
            class="wealth-content__allocation-error"
            title="Erro de carregamento"
            description="Não foi possível carregar o conteúdo. Por favor, tente novamente."
            button-label="Tentar novamente"
            status="alert"
            @click="getProductAllocation"
          />
          <AllocationDetail
            v-else-if="productAllocations"
            :allocations="productAllocations"
            :product-allocations="productAllocations"
          />
        </div>
      </NavigationPanel>
    </NavigationPanelWrapper>
  </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { RouteMixin } from '@/modules/common/mixins/route';

import {
  BaseIcon,
  Row,
  Heading,
  SpinnerLoader,
  HelperViewStatus,
  InitialAvatarIcon,
  ContentDisplay,
  ListDisplay,
  SkeletonLoader
} from '@warrenbrasil/nebraska-web';
import { Money } from '@/modules/common/components/Money';
import {
  NavigationPanelWrapper,
  NavigationPanel
} from '@/modules/common/components/NavigationPanel';
import { BackButton } from '@/modules/common/components/buttons/BackButton';

import { PortfoliosList } from '@/modules/wealth/views/Wealth/components/PortfoliosList';
import { AllocationDetail } from '@/modules/wealth/views/Wealth/components/AllocationDetail';
import { Feature } from '@/modules/common/components/Feature';
import { FeatureFlags } from '@/types/models/FeatureFlags';

import { trackAllocationEvent, trackGoToBuilder } from './track-events';
import { WealthSummary } from '@/modules/wealth/components/WealthSummary';
import {
  getPortfoliosProductsAllocations,
  getProductAllocations,
  IProductAllocationsResponse
} from '@/modules/wealth/services/investment';

import { getHeroBalanceAndButtons } from '@/modules/home/services/balances';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';

@Component({
  components: {
    WealthSummary,
    BackButton,
    BaseIcon,
    Heading,
    PortfoliosList,
    NavigationPanel,
    NavigationPanelWrapper,
    Row,
    Feature,
    AllocationDetail,
    SpinnerLoader,
    HelperViewStatus,
    InitialAvatarIcon,
    ContentDisplay,
    ListDisplay,
    Money,
    SkeletonLoader
  }
})
export default class WealthContent extends mixins(RouteMixin) {
  @Prop({ type: Boolean, default: true })
  private readonly showCreateWalletButton!: boolean;

  isProductAllocationsLoading = false;
  hasProductAllocationsError = false;

  private productAllocations: IProductAllocationsResponse | null = null;

  isAccountAmountLoading = false;
  hasAccountAmountError = false;

  accountAmount = '';

  private get FeatureFlags() {
    return FeatureFlags;
  }

  async getProductAllocation() {
    this.hasProductAllocationsError = false;
    this.isProductAllocationsLoading = true;
    try {
      this.productAllocations = this.enableBffInvestmentMigration
        ? await getPortfoliosProductsAllocations()
        : await getProductAllocations();
    } catch (error) {
      console.error(error);
      this.hasProductAllocationsError = true;
    } finally {
      this.isProductAllocationsLoading = false;
    }
  }

  async fetchAccountAmount() {
    try {
      this.isAccountAmountLoading = true;
      this.hasAccountAmountError = false;

      const { accountBalance } = await getHeroBalanceAndButtons();

      this.accountAmount = accountBalance;
    } catch {
      this.hasAccountAmountError = true;
    } finally {
      this.isAccountAmountLoading = false;
    }
  }

  private async created() {
    await Promise.all([this.getProductAllocation(), this.fetchAccountAmount()]);
  }

  private goToBuilder() {
    trackGoToBuilder();

    this.$router.push({ name: 'catalog' });
  }

  private trackEvent() {
    trackAllocationEvent();
  }

  get enableBffInvestmentMigration() {
    return FeatureFlagsModule.isEnabled(
      FeatureFlags.ENABLE_BFF_INVESTMENTS_MIGRATION
    );
  }
}
</script>

<style lang="less" scoped>
.wealth-content {
  margin: 0 calc(@size-spacing-x300 * -1);
  padding: 0 @size-spacing-x300;
  overflow: hidden;

  &__account-amount {
    margin-bottom: @size-spacing-x800;

    &__loader {
      width: 100%;
      height: 76px;
      margin-bottom: @size-spacing-x800;
    }
  }

  section {
    overflow: initial;
    z-index: auto;
  }

  &__allocation-error {
    margin-top: @size-spacing-x500;
  }

  &__allocation-loader {
    display: flex;
    justify-content: center;
    margin-top: @size-spacing-x500;
  }

  &__icon {
    color: @element-primary;
    background: @background-tertiary;
  }
}

.wealth-content__section {
  padding-bottom: @size-spacing-x800;
}

.wealth-content__navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding-bottom: @size-spacing-x400;

  border-bottom: 1px solid @divider-primary;
}

.wealth-content__section__label-empty {
  margin-bottom: 20px;
  display: flex;
  align-items: left;
  color: @element-secondary;
  border-top: 1px solid @divider-primary;
  padding-top: @size-spacing-x350;
  white-space: nowrap;
}

.wealth-content__navbar__box {
  width: 32px;
  height: 32px;
  pointer-events: none;
}

.portfolio-actions {
  margin-top: @size-spacing-x500;
}
</style>
